import type { Placement } from '@floating-ui/react'
import { observer } from 'mobx-react-lite'
import type { CSSProperties, FC } from 'react'
import { useCallback } from 'react'
import { Modal } from '../../../../../components/modal'
import type { CreateDataGrantModalStore } from './modal-store'
import { CreateDataGrantModalStep } from './modal-store'
import { useOrganization } from '../../../../../stores/organization-store'
import type { Stores } from '../../../../../util/store'

export interface CreateDataGrantsModalProps {
    anchor?: HTMLElement
    placement?: Placement
    onClose(): void
    style?: CSSProperties
    containerStyle?: CSSProperties
    stores: Stores
    store: CreateDataGrantModalStore
    providerOrganizationId: string
}

const headingStyle: CSSProperties = {
    color: 'var(--comment-color)',
}

const selectStyle: CSSProperties = {
    color: 'var(--green-color)',
    cursor: 'pointer',
}

const organizationStyle: CSSProperties = {
    color: 'var(--purple-color)',
    cursor: 'pointer',
}

const errorStyle: CSSProperties = {
    color: 'var(--red-color)',
    cursor: 'pointer',
}

const submitStyle = (store: CreateDataGrantModalStore): CSSProperties => ({
    color: store.isReadyForSubmission
        ? 'var(--purple-color)'
        : 'var(--comment-color)',
    cursor: store.isReadyForSubmission ? 'pointer' : 'default',
})

export const CreateDataGrantIndexModal: FC<CreateDataGrantsModalProps> =
    observer(
        ({
            anchor,
            placement,
            onClose,
            style,
            containerStyle,
            stores,
            store,
            providerOrganizationId,
        }) => {
            const goToConsumerOrganization = useCallback(() => {
                store.setStep(CreateDataGrantModalStep.ConsumerOrganization)
            }, [])

            const submit = useCallback(() => {
                store.submit()
            }, [])

            const consumerOrganization = useOrganization(
                stores.organizations,
                store.consumerOrganizationId,
            )

            return (
                <Modal
                    anchor={anchor}
                    placement={placement}
                    onClose={onClose}
                    style={style}
                    containerStyle={containerStyle}>
                    <div>
                        <div style={headingStyle}>To organization</div>

                        {consumerOrganization && (
                            <div
                                onClick={goToConsumerOrganization}
                                style={organizationStyle}>
                                {consumerOrganization.name}
                            </div>
                        )}

                        {!consumerOrganization && (
                            <div
                                onClick={goToConsumerOrganization}
                                style={selectStyle}>
                                Select
                            </div>
                        )}
                    </div>

                    <div>
                        <div style={headingStyle}>Notes</div>

                        <textarea
                            value={store.notes}
                            onChange={(e) => store.setNotes(e.target.value)}
                            rows={10}
                            style={{
                                width: '100%',
                                padding: '0',
                                border: 'none',
                                boxSizing: 'border-box',
                                fontSize: '1rem',
                                outline: 'none',
                                backgroundColor: 'transparent',
                                color: 'var(--foreground-color)',
                                resize: 'none',
                                overflowY: 'auto',
                            }}
                        />
                    </div>

                    <hr
                        style={{
                            width: '100%',
                            borderTop:
                                '1px solid var(--selection-color-opaque)',
                            borderBottom: 'none',
                        }}
                    />

                    {!!store.consumerOrganizationId &&
                        !!providerOrganizationId &&
                        store.consumerOrganizationId ===
                            providerOrganizationId && (
                            <p style={errorStyle}>
                                The provider and consumer organizations cannot
                                be the same.
                            </p>
                        )}

                    <span style={submitStyle(store)} onClick={submit}>
                        Submit
                    </span>
                </Modal>
            )
        },
    )
